import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

import { Seo } from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { CtaBand } from "@/components/Section";
import { IMAGES } from "@/data/site";

// Uniform image ratio across the whole page.
const RATIO = "aspect-[4/3]";

// Approved content — presented verbatim, only organized/formatted.
const CATEGORIES = [
  {
    key: "A",
    title: "Detailed Janitorial Cleaning",
    items: ["Nightly / Weekly", "Daily Assigned Porter", "Restroom Santizing", "Window Cleaning", "Pressure Washing"],
    images: [{ src: IMAGES.corridor, alt: "Janitorial cart and porter maintaining a bright commercial corridor" }],
  },
  {
    key: "B",
    title: "Post Construction Cleaning",
    group: {
      name: "OCS Builders Group",
      projectsLabel: "Projects",
      projects: ["CVS", "Burlington", "Stop & Shop", "Penske Automotive", "Chick Fil A", "Sprouts"],
    },
    items: [],
    images: [{ src: IMAGES.floorMachine, alt: "Commercial auto-scrubber detailing a retail sales floor" }],
  },
  {
    key: "C",
    title: "Handyman Services",
    items: ["Light Painting", "Lot Cleaning", "Gutter Cleaning", "Asphalt Sealcoating"],
    images: [
      { src: IMAGES.painting, alt: "Light painting during a commercial facility refresh" },
      { src: IMAGES.striping, alt: "Parking-lot line marking as supporting facility maintenance" },
    ],
  },
  {
    key: "D",
    title: "Duct Cleaning",
    items: [
      "OCS Builders Group",
      "Retail Project Management",
      "Structure Craft",
      "DGC Capital",
      "Nurminen Construction",
      "Old New England Construction",
    ],
    projectsLabel: "Projects",
    projects: ["Stop & Shop", "Pet Smart"],
    images: [],
  },
  {
    key: "E",
    title: "Dealership Shop Mechanical Repair",
    items: ["Compressor maintenance", "Reel replacement"],
    images: [{ src: IMAGES.plumbing, alt: "General mechanical-maintenance repair on chrome fixtures" }],
  },
];

// Bottom photography strip — uniform sizing.
const PHOTO_STRIP = [
  { src: IMAGES.officeMop, alt: "Commercial office cleaning team at work" },
  { src: IMAGES.moodMop, alt: "Wet-floor detailing in a commercial facility" },
  { src: IMAGES.carpetCare, alt: "Carpet extraction and cleaning in progress" },
  { src: IMAGES.floorMachine, alt: "Commercial floor scrubber in a retail facility" },
];

const ItemList = ({ items }) => (
  <ul className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2">
    {items.map((it) => (
      <li key={it} className="flex items-start gap-3">
        <span className="mt-2 h-1.5 w-1.5 flex-none bg-brand-green" />
        <span className="text-base leading-relaxed text-navy">{it}</span>
      </li>
    ))}
  </ul>
);

const ProjectGroup = ({ group }) => (
  <div className="mt-6 border-l-2 border-brand-green pl-6">
    <p className="font-display text-lg font-bold tracking-tight text-navy">{group.name}</p>
    <p className="mt-4 overline">{group.projectsLabel}</p>
    <ul className="mt-3 grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {group.projects.map((p) => (
        <li key={p} className="flex items-start gap-3">
          <span className="mt-2 h-1.5 w-1.5 flex-none bg-brand-green" />
          <span className="text-base leading-relaxed text-navy">{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CategoryRow = ({ cat, index }) => {
  const flip = index % 2 === 1;
  return (
    <Reveal>
      <article data-testid={`service-category-${cat.key}`} className="grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
        {/* Media */}
        <div className={flip ? "lg:order-2" : ""}>
          {cat.images.length === 2 ? (
            <div className="grid grid-cols-2 gap-4">
              {cat.images.map((img) => (
                <SmartImage key={img.src} src={img.src} alt={img.alt} ratio={RATIO} />
              ))}
            </div>
          ) : (
            <SmartImage src={cat.images[0].src} alt={cat.images[0].alt} ratio={RATIO} />
          )}
        </div>
        {/* Content */}
        <div className={flip ? "lg:order-1" : ""}>
          <div className="flex items-center gap-4">
            <span className="flex h-10 w-10 items-center justify-center bg-navy font-display text-base font-black text-white">
              {cat.key}
            </span>
            <span className="overline">Service Category {cat.key}</span>
          </div>
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
            {cat.title}
          </h2>
          {cat.group ? <ProjectGroup group={cat.group} /> : <ItemList items={cat.items} />}
        </div>
      </article>
    </Reveal>
  );
};

const DuctCard = ({ cat }) => (
  <Reveal>
    <div data-testid={`service-category-${cat.key}`} className="border border-border bg-cool-gray p-8 sm:p-12 lg:p-16">
      <div className="flex items-center gap-4">
        <span className="flex h-10 w-10 items-center justify-center bg-brand-green font-display text-base font-black text-navy">
          {cat.key}
        </span>
        <span className="overline">Service Category {cat.key}</span>
      </div>
      <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">{cat.title}</h2>
      <ul className="mt-8 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
        {cat.items.map((it) => (
          <li key={it} className="flex items-start gap-3 border-t border-navy/10 pt-4">
            <span className="mt-2 h-1.5 w-1.5 flex-none bg-brand-green" />
            <span className="text-base leading-relaxed text-navy">{it}</span>
          </li>
        ))}
      </ul>
      {cat.projects && (
        <div className="mt-10 border-l-2 border-brand-green pl-6">
          <p className="overline">{cat.projectsLabel}</p>
          <ul className="mt-3 grid gap-x-10 gap-y-3 sm:grid-cols-2">
            {cat.projects.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 flex-none bg-brand-green" />
                <span className="text-base leading-relaxed text-navy">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  </Reveal>
);

export default function Services() {
  return (
    <>
      <Seo
        title="Commercial Cleaning Services"
        description="Detailed janitorial cleaning, post-construction cleaning, handyman services, duct cleaning and dealership shop mechanical repair for commercial facilities."
        path="/services"
      />

      {/* Hero */}
      <section data-testid="services-hero" className="relative overflow-hidden bg-navy noise-overlay">
        <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-brand-green/20 blur-3xl" />
        <div className="container-px relative z-[2] mx-auto max-w-[1500px] pb-14 pt-16 sm:pt-20">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="overline text-brand-green">
            Services
          </motion.span>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.2}>Commercial Cleaning</MaskLine>
            <MaskLine delay={0.32}>Services</MaskLine>
          </h1>
        </div>
      </section>

      {/* Feature image — commercial cleaning team */}
      <section className="container-px mx-auto max-w-[1500px] py-10 sm:py-14">
        <Reveal>
          <SmartImage
            src={IMAGES.officeMop}
            alt="Precision Maintenance commercial cleaning team working in a modern facility"
            ratio="aspect-[21/9]"
            eager
          />
        </Reveal>
      </section>

      {/* Categories */}
      <section className="container-px mx-auto max-w-[1500px] pb-8">
        <div className="flex flex-col gap-20 lg:gap-28">
          {CATEGORIES.map((cat, i) =>
            cat.key === "D" ? <DuctCard key={cat.key} cat={cat} /> : <CategoryRow key={cat.key} cat={cat} index={i} />
          )}
        </div>
      </section>

      {/* Additional photography */}
      <section className="container-px mx-auto max-w-[1500px] py-20">
        <Reveal>
          <span className="overline">In the field</span>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {PHOTO_STRIP.map((p, i) => (
            <Reveal key={i} delay={(i % 4) * 0.08}>
              <SmartImage src={p.src} alt={p.alt} ratio={RATIO} imgClassName="hover:scale-105" />
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBand
        title="Request a Customized Cleaning Plan"
        primary={{ label: "Request a Customized Cleaning Plan", to: "/request-a-quote" }}
        secondary={{ label: "Schedule a Facility Walkthrough", to: "/request-a-quote" }}
      />
    </>
  );
}
